import React, { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ContentPage from 'components/ContentView/ContentPage/ContentPage';
import useDashboardsQuery from './useDashboardsQuery';
import { useOrgIdSelect } from 'components/Select/useOrgIdSelect';
import _ from 'lodash';
import createMutationRequest from 'utils/createMutationRequest';
import createQueryRequest from 'services/createQueryRequest';
import useAPI from 'services/useAPI';
import { orgKeys } from 'components/Admin/Organizations/queries';
import { Button, Flex, FlexItem, Form, FormGroup, Skeleton, Spinner } from '@patternfly/react-core';
import SimpleTable from 'components/Common/SimpleTable/SimpleTable';
import Tl from 'localization/LocalizationKeys';
import { useTranslation } from 'localization/useTranslation';
import useBoolean from 'utils/hooks/useBoolean';
import styles from './HomeDashboardEditor.module.css';
import SelectDashboardsModal from 'components/Modals/SelectDashboardsModal';

const columns = [
  {
    label: Tl.common.id,
    path: 'id'
  },
  {
    label: Tl.common.name,
    path: 'displayName'
  },
  {
    label: Tl.common.assets,
    path: 'data?.assets'
  }
];

const sortableAndSearchableColumns = columns.map(column => {
  if (column.path === 'displayName') {
    return {
      ...column,
      isSortable: true,
      searchItem: true
    };
  }
  return {
    ...column,
    isSortable: true
  };
});

const HomeDashboardEditor = ({ breadcrumbs }) => {
  const dashboardQuery = useDashboardsQuery();
  const { renderOrgIdSelect, currentOrg, numberOfOrgs } = useOrgIdSelect({
    // onChange: null, label: false, neededPrivilege: privileges.OrganizationManagement
    onChange: null, label: false
  });
  const queryClient = useQueryClient();
  const api = useAPI();

  const orgSettingsQuery = useQuery({
    enabled: !!currentOrg?.id,
    queryKey: orgKeys.orgSettings(currentOrg?.id),
    queryFn: createQueryRequest(api.core.getOrganizationSettings)
  });

  const updateOrgSettingsMutation = useMutation({
    mutationFn: createMutationRequest(api.core.updateOrganizationSettings),
    onMutate: (newSettings) => {
      // Optimistic update
      const oldOrgSettings = queryClient.getQueryData(orgKeys.orgSettings(currentOrg?.id));
      queryClient.setQueryData(orgKeys.orgSettings(currentOrg?.id), newSettings);
      return { newSettings, oldOrgSettings };
    },
    onSuccess: () => {
      queryClient.invalidateQueries(orgKeys.orgSettings(currentOrg?.id));
    },
    onError: (unusedError, unusedVariables, context) => {
      // Readd the old data
      queryClient.setQueryData(orgKeys.orgSettings(currentOrg?.id), context?.oldOrgSettings);
    }
  });

  const isLoading = dashboardQuery.isLoading || orgSettingsQuery.isLoading;

  const homeDashboardIds = useMemo(() => {
    // Temp fix for dashboard ids that were stored as full dashboard objects
    return _.map(orgSettingsQuery.data?.homeDashboards,
      dashboardId => _.isObject(dashboardId) ? dashboardId.id : dashboardId);
  }, [orgSettingsQuery.data?.homeDashboards]);

  const allSelectableDashboards = useMemo(() => {
    return _.filter(dashboardQuery.data, dashboard => !dashboard.data.hasDynamicAssets);
  }, [dashboardQuery.data]);

  const homeDashboards = useMemo(() => {
    // Static dashboards that are selected for home
    return _.filter(allSelectableDashboards, dashboard => _.includes(homeDashboardIds, dashboard.id));
  }, [allSelectableDashboards, homeDashboardIds]);

  const updateSettingsWithDashboards = useCallback((newHomeDashboardIds) => {
    const settings = {
      orgId: currentOrg?.id,
      ...orgSettingsQuery.data,
      homeDashboards: newHomeDashboardIds
    };
    updateOrgSettingsMutation.mutate(settings);
  }, [currentOrg?.id, orgSettingsQuery.data, updateOrgSettingsMutation]);

  const onDataOrderChanged = useCallback((newDashboardOrder) => {
    updateSettingsWithDashboards(_.map(newDashboardOrder, dashboard => dashboard?.id));
  }, [updateSettingsWithDashboards]);
  const [isShowingDashboardSelection, , closeDashboardSelection, toggleDashboardSelection] = useBoolean(false);

  const onSelectedDashboards = useCallback((selectedRows) => {
    const selectedDashboardIds = _.map(selectedRows, row => row?.id);
    updateSettingsWithDashboards(selectedDashboardIds);
  }, [updateSettingsWithDashboards]);
  const T = useTranslation();

  const renderOrgForm = useCallback(() => {
    if (numberOfOrgs === 1) {
      return null;
    }
    return (
      <FormGroup className={styles.orgSelector} label={T.common.organization} fieldId={'orgId'}>
        <Flex>
          <FlexItem grow={{ default: 'grow' }}>{ renderOrgIdSelect() }</FlexItem>
          { isLoading && <Spinner isInline /> }
        </Flex>
      </FormGroup>
    );
  }, [isLoading, numberOfOrgs, renderOrgIdSelect, T.common.organization]);

  return (
    <ContentPage breadcrumb={breadcrumbs} >
      <Form className={styles.homedashboardForm} >
        { renderOrgForm() }
        { /* <FormGroup className={styles.orgSelector} label={T.common.organization} fieldId={'orgId'}>
          <Flex>
            <FlexItem grow={{ default: 'grow' }}>{ renderOrgIdSelect() }</FlexItem>
            { isLoading && <Spinner isInline /> }
          </Flex>
        </FormGroup> */ }
        <FormGroup>
          <Button isDisabled={isLoading} onClick={toggleDashboardSelection}>{ T.admin.dashboard.selectdashboards }</Button>
        </FormGroup>

        <FormGroup className={styles.tableFormGroup} fieldId={'dashboards'} label={T.admin.dashboard.homedashboards} >
          { isLoading && <Skeleton /> }
          { !isLoading && <SimpleTable
            data={homeDashboards}
            columns={columns}
            rowIteratee={_.iteratee('id')}
            isDraggable
            onDataOrderChanged={onDataOrderChanged}
          /> }
        </FormGroup>
      </Form>
      <SelectDashboardsModal
        title={T.admin.dashboard.selectdashboards}
        tableColumns={sortableAndSearchableColumns}
        isOpen={isShowingDashboardSelection}
        onClose={closeDashboardSelection}
        onSelected={onSelectedDashboards}
        available={allSelectableDashboards}
        selected={homeDashboards}
      />
    </ContentPage>
  );
};

export default React.memo(HomeDashboardEditor);
